import React from "react";
import ReactDOM from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import App from "./App";
import { initAuth, loginRequest, b2cPolicies, relayState } from "./authConfig";
import { AuthProvider } from "./context/AuthContext";
import { ThemeContextProvider } from "./context/ThemeContext";
import { BreadcrumbProvider } from "./context/BreadcrumbContext";
import { SYSTEM_NAME, applyRuntimeConfig } from "./config";
import { loadRuntimeConfig } from "./runtimeConfig";
import ErrorBoundary from "./components/ErrorBoundary";
import { initializeAppInsights } from "./telemetry/appInsights";
import "./styles/global.css";

function renderBootstrapError(error) {
  console.error("Failed to start application:", error);
  const container = document.getElementById("root");
  if (!container) return;
  container.innerHTML = `
    <div style="font-family: sans-serif; max-width: 560px; margin: 4rem auto; padding: 1.5rem; border: 1px solid #ddd; border-radius: 8px;">
      <h1 style="font-size: 1.25rem; margin: 0 0 0.5rem;">XLerate could not start</h1>
      <p style="margin: 0 0 1rem; color: #444;">
        The application configuration could not be loaded from the server. Please try again in a moment.
        If the problem continues, contact IT support.
      </p>
      <button type="button" onclick="window.location.reload()" style="padding: 0.5rem 1rem; cursor: pointer;">&#8635; Retry</button>
    </div>
  `;
}

export async function bootstrapStaff() {
  let runtimeConfig;
  try {
    runtimeConfig = await loadRuntimeConfig();
  } catch (error) {
    renderBootstrapError(error);
    return;
  }

  applyRuntimeConfig(runtimeConfig);
  const msalInstance = initAuth(runtimeConfig);

  document.title = SYSTEM_NAME;
  initializeAppInsights();

  window.__msal = msalInstance;
  await msalInstance.initialize();

  try {
    const response = await msalInstance.handleRedirectPromise();

    if (response?.error?.message?.includes("AADB2C90118")) {
      await msalInstance.loginRedirect({
        ...loginRequest,
        authority: b2cPolicies.authorities.resetPassword.authority,
        state: relayState(),
      });
      return;
    }
  } catch (e) {
    const msg = e?.message || e?.errorMessage || "";
    if (msg.includes("AADB2C90118")) {
      await msalInstance.loginRedirect({
        ...loginRequest,
        authority: b2cPolicies.authorities.resetPassword.authority,
        state: relayState(),
      });
      return;
    }
    console.error("handleRedirectPromise error:", e);
  }

  const container = document.getElementById("root");
  const root = ReactDOM.createRoot(container);

  root.render(
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <ThemeContextProvider>
            <BreadcrumbProvider>
              <App />
            </BreadcrumbProvider>
          </ThemeContextProvider>
        </AuthProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
